import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';

const BasePathContext = createContext('');
const PathContext = createContext(null);

let ssrPath = '/';
let isNode = true; // eslint-disable-line import/no-mutable-exports

try {
  isNode = window === undefined;
} catch (e) {} // eslint-disable-line no-empty
function getSsrPath() {
  return ssrPath;
}

function isFunction(obj) {
  return obj && typeof obj === 'function';
}

function usePath(basePath) {
  const contextPath = useContext(PathContext);
  const contextBasePath = useBasePath(); // hooks can't be called conditionally

  basePath = basePath || contextBasePath;
  const [path, setPath] = useState(getCurrentPath(basePath));
  useLocationChange(setPath, {
    basePath,
    inheritBasePath: !basePath
  });
  return (contextPath !== null ? contextPath : path.replace(basePathMatcher(basePath), '')) || '/'; // return path
}
function useBasePath() {
  return useContext(BasePathContext);
}
function useHash({
  stripHash = true
} = {}) {
  const [hash, setHash] = useState(window.location.hash);
  const handleHash = useCallback(() => {
    if (window.location.hash === hash) return;
    setHash(window.location.hash);
  }, [setHash]);
  useEffect(() => {
    window.addEventListener('hashchange', handleHash, false);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [handleHash]);
  useLocationChange(handleHash);
  return stripHash ? hash.substring(1) : hash;
}
function getCurrentPath(basePath = '') {
  return isNode ? getSsrPath() : window.location.pathname.replace(basePathMatcher(basePath), '') || '/';
}
function getCurrentHash() {
  if (isNode) {
    let path = getSsrPath() ;
    let hashIndex = path.indexOf('#');
    return  path.substring(hashIndex) ;
  }

  return window.location.hash;
}
function useLocationChange(setFn, options = {}) {
  if (isNode) return;
  let basePath = '';
  const routerBasePath = useBasePath();
  if (options.inheritBasePath !== false) basePath = routerBasePath;
  if (!basePath && options.basePath) basePath = options.basePath;
  const setRef = useRef(setFn);
  useEffect(() => {
    // setFn could be an in-render declared callback, making it unstable
    // This is a method of using an often-changing callback from React Hooks
    // https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
    // While not recommended, it is the best current (16.9) available method
    // For reducing the useEffect cleanup from setFn changing every render
    setRef.current = setFn;
  });
  const onPopState = useCallback(() => {
    // No predicate defaults true
    if (options.isActive !== undefined && !isPredicateActive(options.isActive)) return;
    setRef.current(getCurrentPath(basePath));
  }, [options.isActive, basePath]);
  useEffect(() => {
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onPopState]);
}

function isPredicateActive(predicate) {
  return isFunction(predicate) ? predicate() : predicate;
}

function basePathMatcher(basePath) {
  return new RegExp('^' + basePath, 'i');
}

function useRoutes(routes, {
  basePath = '',
  routeProps = {},
  overridePathParams = true,
  matchTrailingSlash = false
} = {}) {
  // path is the browser url location
  const [path, setPath] = useState(getCurrentPath(basePath));
  useLocationChange(setPath, {
    basePath,
    inheritBasePath: !basePath
  }); // Get the current route

  const route = matchRoute(routes, path, {
    routeProps,
    overridePathParams,
    matchTrailingSlash
  }); // No match should not return an empty Provider, just null

  if (!route) return null;
  return React.createElement(BasePathContext.Provider, {
    value: basePath
  }, React.createElement(PathContext.Provider, {
    value: path
  }, route));
}

function matchRoute(routes, path, {
  routeProps,
  overridePathParams,
  matchTrailingSlash
}) {
  // path.length > 1 ensure we still match on the root route "/" when matchTrailingSlash is set
  if (matchTrailingSlash && path && path[path.length - 1] === '/' && path.length > 1) {
    path = path.substring(0, path.length - 1);
  }

  const routeMatchers = useMemo(() => Object.keys(routes).map(createRouteMatcher), [hashRoutes(routes)]); // Hacky method for find + map

  let routeMatch;
  routeMatchers.find(([routePath, regex, groups]) => {
    const match = path.match(regex);
    if (!match) return null;
    const result = [routePath, {}];

    if (groups && groups.length) {
      result[1] = groups.reduce((props, prop, i) => {
        props[prop] = match[i + 1];
        return props;
      }, {});
    }

    return routeMatch = result;
  });
  if (!routeMatch) return null;
  const [routePath, props] = routeMatch;
  return routes[routePath](overridePathParams ? { ...props,
    ...routeProps
  } : { ...routeProps,
    ...props
  });
}

function createRouteMatcher(routePath) {
  const route = [routePath, new RegExp(`${routePath.substr(0, 1) === '*' ? '' : '^'}${routePath.replace(/:[a-zA-Z]+/g, '([^/]+)').replace(/\*/g, '')}${routePath.substr(-1) === '*' ? '' : '$'}`, 'i')];
  const propList = routePath.match(/:[a-zA-Z]+/g);
  route.push(propList ? propList.map(paramName => paramName.substr(1)) : []);
  return route;
} // React doesn't like when the hook dependency array changes size
// >> Warning: The final argument passed to useMemo changed size between renders. The order and size of this array must remain constant.
// It is recommended to use a hashing function to produce a single, stable value
// https://github.com/facebook/react/issues/14324#issuecomment-441489421


function hashRoutes(routes) {
  const paths = Object.keys(routes).sort();
  return paths.join(':');
}

const defaultPrompt = 'Are you sure you want to leave this page?';
const interceptors = new Set();
function navigate(url, replaceOrQuery, replace) {
  if (typeof url !== 'string') {
    throw new Error(`"url" must be a string, was provided a(n) ${typeof url}`);
  }

  if (Array.isArray(replaceOrQuery)) {
    throw new Error('"replaceOrQuery" must be boolean, object, or URLSearchParams');
  }

  if (shouldCancelNavigation()) return;

  if (replaceOrQuery !== null && typeof replaceOrQuery === 'object') {
    url += '?' + new URLSearchParams(replaceOrQuery).toString();
  } else if (replace === undefined && replaceOrQuery !== undefined) {
    replace = replaceOrQuery;
  } else if (replace === undefined && replaceOrQuery === undefined) {
    replace = false;
  }

  window.history[`${replace ? 'replace' : 'push'}State`](null, null, url);
  dispatchEvent(new PopStateEvent('popstate', null));
}
function useNavigationPrompt(predicate = true, prompt = defaultPrompt) {
  if (isNode) return;
  useEffect(() => {
    const handler = e => {
      if (predicate) {
        return e ? cancelNavigation(e, prompt) : prompt;
      }
    };

    addInterceptor(handler);
    return () => removeInterceptor(handler);
  }, [predicate, prompt]);
}
function shouldCancelNavigation() {
  // confirm if any interceptors return true
  return Array.from(interceptors).some(interceptor => {
    let prompt = interceptor();
    if (!prompt) return false; // cancel navigation if user declines

    return !window.confirm(prompt); // eslint-disable-line no-alert
  });
}

function addInterceptor(handler) {
  window.addEventListener('beforeunload', handler);
  interceptors.add(handler);
}

function removeInterceptor(handler) {
  window.removeEventListener('beforeunload', handler);
  interceptors.delete(handler);
}

function cancelNavigation(event, prompt) {
  // Cancel the event as stated by the standard.
  event.preventDefault(); // Chrome requires returnValue to be set.

  event.returnValue = prompt; // Return value for prompt per spec

  return prompt;
}

function useNavigate(optBasePath = '') {
  const basePath = useBasePath();
  const navigateWithBasePath = useCallback((url, replaceOrQuery, replace) => {
    const base = optBasePath || basePath;
    const href = url.startsWith('/') ? base + url : url;
    navigate(href, replaceOrQuery, replace);
  }, [basePath, optBasePath]);
  return navigateWithBasePath;
}

function useQueryParams(parseFn = parseQuery, serializeFn = serializeQuery) {
  const [querystring, setQuerystring] = useState(getQueryString());
  const setQueryParams = useCallback( // TODO: V2 using options param for replace
  (params, replace = true) => {
    let path = getCurrentPath();
    params = replace ? params : { ...parseFn(querystring),
      ...params
    };
    const serialized = serializeFn(params).toString();
    if (serialized) path += '?' + serialized;
    if (!replace) path += getCurrentHash();
    navigate(path);
  }, [querystring]); // Update state when route changes

  useLocationChange(() => setQuerystring(getQueryString()));
  return [parseFn(querystring), setQueryParams];
}

function parseQuery(querystring) {
  return [...new URLSearchParams(querystring)].reduce((result, [key, value]) => {
    result[key] = value;
    return result;
  }, {});
}

function serializeQuery(queryParams) {
  return Object.entries(queryParams).reduce((query, [key, value]) => {
    if (value !== null) query.append(key, value);
    return query;
  }, new URLSearchParams());
}

function getQueryString() {
  if (isNode) {
    let ssrPath = getSsrPath();
    let queryIndex = ssrPath.indexOf('?');
    return queryIndex === -1 ? '' : ssrPath.substring(queryIndex + 1);
  }

  return location.search;
}
function deriveQueryString(current, target, merge) {
  current = queryAsObject(current);
  target = queryAsObject(target);
  return merge ? new URLSearchParams({ ...current,
    ...target
  }).toString() : new URLSearchParams(target).toString();
}

function queryAsObject(query) {
  if (!query) return {};
  return Object.fromEntries(new URLSearchParams(query).entries());
}

function useRedirect(predicateUrl, targetUrl, queryParams = null, replace = true) {
  const currentPath = usePath();
  useEffect(() => {
    if (currentPath === predicateUrl) {
      navigate(targetUrl, queryParams, replace);
    }
  }, [predicateUrl, targetUrl, queryParams, replace, currentPath]);
}
function Redirect({
  to,
  query,
  replace = true,
  merge = true
}) {
  const [currentQuery] = useQueryParams();
  const hash = getCurrentHash();
  const path = usePath();
  let url = to;
  const targetQuery = deriveQueryString(currentQuery, query, merge);

  if (targetQuery) {
    url += '?' + targetQuery;
  }

  if (merge && hash && hash.length) {
    url += hash;
  }

  useRedirect(path, url, null, replace);
  return null;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function Link({
  href,
  basePath,
  linkRef,
  ...props
}) {
  const contextBasePath = useBasePath();
  basePath = basePath || contextBasePath;
  href = getLinkHref(href, basePath);
  const onClick = useCallback(e => {
    try {
      if (props.onClick) props.onClick(e);
    } catch (ex) {
      e.preventDefault();
      throw ex;
    }

    if (shouldTrap(e, props)) {
      e.preventDefault(); // prevent the link from actually navigating

      navigate(e.currentTarget.href);
    }
  }, [basePath, href, props.onClick]); // TODO: 2.0 - replace linkRef with forwardRef API

  return React.createElement("a", _extends({}, props, {
    href: href,
    onClick: onClick,
    ref: linkRef
  }));
}
function ActiveLink(props) {
  const basePath = useBasePath();
  const path = usePath(basePath);
  const href = getLinkHref(props.href, basePath);
  let {
    className,
    activeClass,
    exactActiveClass,
    ...rest
  } = props;
  if (!className) className = '';
  if (exactActiveClass && path === href) className += ` ${exactActiveClass}`;
  if (activeClass && path.startsWith(href)) className += ` ${activeClass}`;
  return React.createElement(Link, _extends({}, rest, {
    className: className
  }));
}

function getLinkHref(href, basePath = '') {
  return href.substring(0, 1) === '/' ? basePath + href : href;
}

function shouldTrap(e, props) {
  let {
    target
  } = props;
  return !e.defaultPrevented && // onClick prevented default
  e.button === 0 && // ignore everything but left clicks
  !(target || target === '_self') && // dont trap target === blank
  !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey);
}

export { ActiveLink, Link, Redirect, navigate, useBasePath, useHash, useLocationChange, useNavigate, useNavigationPrompt, usePath, useQueryParams, useRedirect, useRoutes };
